// webapp/src/Profile.js
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "./supabase";
import { useAuth } from "./AuthContext";
import Modal from "./Modal";
import { redirectToCheckout } from "./utils/stripe.js";
import {
  FiUser,
  FiMail,
  FiLock,
  FiLogOut,
  FiAlertCircle,
  FiRefreshCw,
  FiTrash2,
  FiCheckCircle,
  FiCreditCard,
  FiList,
  FiLink,
  FiSettings,
  FiSliders,
  FiInfo,
} from "react-icons/fi";
import "./Profile.css";
import { useScreenBuilder } from "./ScreenBuilderContext";
import BacktestConfigsPage from "./components/backtestConfigs/BacktestConfigsPage.js";
import WatchlistEditor from "./components/watchlist/WatchlistEditor.js";
import { fetchShareLinks, deleteShareLink } from "./lib/api/shareLinks";

// Resolve and sanitize API base (strip trailing slash)
// Prioritize environment variables over hardcoded config
const rawBase =
  process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_API_BASE ||
  process.env.VITE_API_BASE ||
  "http://127.0.0.1:4998";
const API_BASE = rawBase.replace(/\/+$/, "");

function Profile() {
  const [currentUser, setCurrentUser] = useState(null);
  const [quota, setQuota] = useState(null);
  const [isPremium, setIsPremium] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [responseMessage, setResponseMessage] = useState(null);

  const [modalTitle, setModalTitle] = useState("");
  const [modalContent, setModalContent] = useState("");
  const [showModal, setShowModal] = useState(false);

  // User settings state
  const [userSettings, setUserSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [debugTickerEditing, setDebugTickerEditing] = useState(false);
  const [debugTickerDraft, setDebugTickerDraft] = useState("");
  const [symbols, setSymbols] = useState([]);
  const [symbolsLoading, setSymbolsLoading] = useState(false);
  const [symbolsError, setSymbolsError] = useState(null);
  const [activeSection, setActiveSection] = useState("backtest-configs");
  const [shareLinks, setShareLinks] = useState([]);
  const [shareLinksLoading, setShareLinksLoading] = useState(false);
  const [shareLinksError, setShareLinksError] = useState(null);
  const [shareLinksDeleting, setShareLinksDeleting] = useState({});

  // Premium plan selection state
  const [isLoadingPlan, setIsLoadingPlan] = useState(null);
  const [hasUsedTrial, setHasUsedTrial] = useState(false);

  const navigate = useNavigate();
  const {
    resetReauthenticationStatus,
    setNextPageAfterReauth,
    checkPremiumStatus,
    isPremium: contextIsPremium,
  } = useAuth();
  const {
    devModeEnabled,
    setDevModeEnabled,
    expertModeEnabled,
    setExpertModeEnabled
  } = useScreenBuilder();

  // Authorized fetch to Flask backend
  const apiFetch = useCallback(async (path, options = {}) => {
    const { data } = await supabase.auth.getSession();
    const token = data?.session?.access_token;
    if (!token) throw new Error("No active session");

    const res = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers: {
        ...(options.headers || {}),
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    const text = await res.text(); // handle empty/204 safely
    let body = null;
    try {
      body = text ? JSON.parse(text) : null;
    } catch {
      body = null;
    }
    if (!res.ok) {
      const msg = body?.error || body?.message || res.statusText;
      throw new Error(msg);
    }
    return body;
  }, []);

  // Get user data
  useEffect(() => {
    async function fetchUserData() {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (user) {
        const isEmailSignIn = user.app_metadata?.provider === "email" || false;
        setCurrentUser({
          email: user.email,
          emailVerified: user.email_confirmed_at !== null,
          isEmailSignIn,
        });
      } else {
        setCurrentUser(null);
      }
    }
    fetchUserData();
  }, []);

  // Premium from context
  useEffect(() => {
    setIsPremium(contextIsPremium);
  }, [contextIsPremium]);

  // Check if user has already used their free trial
  const checkTrialStatus = useCallback(async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('profiles')
        .select('trial_consumed')
        .eq('id', user.id)
        .single();

      if (!error && data) {
        setHasUsedTrial(data.trial_consumed === true);
      }
    } catch (err) {
      console.error('Error checking trial status:', err);
    }
  }, []);

  useEffect(() => {
    if (currentUser) {
      checkTrialStatus();
    }
  }, [currentUser, checkTrialStatus]);

  // Stripe success polling
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get("session_id");
    if (sessionId) {
      const delays = [1000, 3000, 5000, 8000];
      delays.forEach((d) => setTimeout(() => checkPremiumStatus(true), d));
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [checkPremiumStatus]);

  // --- QUOTA: call backend /quota
  const fetchQuota = useCallback(async () => {
    try {
      const data = await apiFetch("/quota", { method: "GET" });
      setQuota(data);
    } catch (error) {
      console.error("Error fetching quota:", error);
    }
  }, [apiFetch]);

  useEffect(() => {
    if (currentUser) {
      fetchQuota();
    }
  }, [currentUser, fetchQuota]);

  // --- USER SETTINGS
  const loadUserSettings = useCallback(async () => {
    if (!currentUser) return;
    setSettingsLoading(true);
    try {
      const data = await apiFetch("/api/user/settings", { method: "GET" });
      setUserSettings(data?.settings || null);
    } catch (err) {
      console.error("Error loading user settings:", err);
      setUserSettings(null);
    } finally {
      setSettingsLoading(false);
    }
  }, [apiFetch, currentUser]);

  const handleToggleOnboarding = async () => {
    if (!userSettings) return;
    const newValue = !userSettings.onboarding_enabled;

    try {
      const data = await apiFetch("/api/user/settings", {
        method: "PUT",
        body: JSON.stringify({ onboarding_enabled: newValue }),
      });
      setUserSettings(data?.settings || userSettings);
    } catch (err) {
      setModalTitle("Settings Error");
      setModalContent(err.message || "Failed to update onboarding setting.");
      setShowModal(true);
    }
  };

  const handleToggleDevMode = () => {
    setDevModeEnabled(!devModeEnabled);
  };

  const handleToggleExpertMode = () => {
    setExpertModeEnabled(!expertModeEnabled);
  };

  const loadSymbols = useCallback(async () => {
    if (symbols.length > 0) return;
    setSymbolsLoading(true);
    setSymbolsError(null);
    try {
      const response = await fetch(`${API_BASE}/api/symbols`);
      if (!response.ok) throw new Error("Failed to load symbols");
      const data = await response.json();
      setSymbols(data.symbols || []);
    } catch (err) {
      setSymbolsError(err.message || "Failed to load symbols");
    } finally {
      setSymbolsLoading(false);
    }
  }, [symbols.length]);

  const loadShareLinks = useCallback(async () => {
    if (!currentUser) return;
    setShareLinksLoading(true);
    setShareLinksError(null);
    try {
      const data = await fetchShareLinks();
      setShareLinks(Array.isArray(data) ? data : []);
    } catch (err) {
      setShareLinksError(err.message || "Failed to load share links");
    } finally {
      setShareLinksLoading(false);
    }
  }, [currentUser]);

  const handleDeleteShareLink = async (shareId) => {
    const previousLinks = shareLinks;
    setShareLinks((prev) => prev.filter((link) => link.id !== shareId));
    setShareLinksDeleting((prev) => ({ ...prev, [shareId]: true }));
    try {
      await deleteShareLink(shareId);
    } catch (err) {
      setShareLinks(previousLinks);
      setModalTitle("Share Link Error");
      setModalContent(err.message || "Failed to delete share link.");
      setShowModal(true);
    } finally {
      setShareLinksDeleting((prev) => {
        const next = { ...prev };
        delete next[shareId];
        return next;
      });
    }
  };

  const filteredSymbols = useMemo(() => {
    if (!symbols.length) return [];
    const term = debugTickerDraft.trim().toLowerCase();
    const shortlist = symbols.filter((symbol) => {
      const ticker = symbol.ticker?.toLowerCase() || "";
      const company = symbol.company_name?.toLowerCase() || "";
      const sector = symbol.sector?.toLowerCase() || "";
      return !term || ticker.includes(term) || company.includes(term) || sector.includes(term);
    });
    return shortlist.slice(0, 20);
  }, [symbols, debugTickerDraft]);

  const handleStartDebugTickerEdit = async () => {
    if (!userSettings) return;
    setDebugTickerDraft(userSettings.debug_ticker || "AAPL");
    setDebugTickerEditing(true);
    await loadSymbols();
  };

  const handleCancelDebugTickerEdit = () => {
    setDebugTickerEditing(false);
    setDebugTickerDraft(userSettings?.debug_ticker || "AAPL");
  };

  const handleSaveDebugTicker = async () => {
    if (!debugTickerDraft.trim()) return;
    try {
      const payload = { debug_ticker: debugTickerDraft.trim().toUpperCase() };
      const data = await apiFetch("/api/user/settings", {
        method: "PUT",
        body: JSON.stringify(payload),
      });
      setUserSettings(data?.settings || userSettings);
      setDebugTickerEditing(false);
    } catch (err) {
      setModalTitle("Settings Error");
      setModalContent(err.message || "Failed to update debug ticker.");
      setShowModal(true);
    }
  };

  useEffect(() => {
    if (currentUser) loadUserSettings();
  }, [currentUser, loadUserSettings]);

  useEffect(() => {
    if (currentUser && activeSection === "shared-links") {
      loadShareLinks();
    }
  }, [currentUser, activeSection, loadShareLinks]);

  // Sign out
  const handleSignOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) console.error("Sign out error:", error);
    else {
      resetReauthenticationStatus();
      navigate("/login");
    }
  };

  // Email verification
  const requestEmailVerification = async () => {
    if (currentUser && !currentUser.emailVerified) {
      try {
        const { error } = await supabase.auth.resend({
          type: "signup",
          email: currentUser.email,
        });
        if (error) throw error;
        alert("Verification email sent.");
      } catch (error) {
        console.error("Error sending verification email:", error);
      }
    }
  };

  // Change email/password
  const handleChangePasswordClick = () => {
    if (currentUser?.isEmailSignIn) {
      setNextPageAfterReauth("/dashboard/new-password");
      navigate("/dashboard/reauthenticate");
    }
  };

  const handleChangeEmailClick = () => {
    if (currentUser?.isEmailSignIn) {
      setNextPageAfterReauth("/dashboard/change-email");
      navigate("/dashboard/reauthenticate");
    }
  };

  // Reset account (calls backend /reset_account)
  const handleResetAccountData = () => {
    setIsModalOpen(true);
    setResponseMessage(null);
  };

  const handleResetConfirm = async () => {
    setIsProcessing(true);
    try {
      const data = await apiFetch("/reset_account", { method: "POST" });
      setResponseMessage({
        type: "success",
        text: data.message || "Your account data has been reset successfully.",
      });
    } catch (error) {
      console.error("Error resetting account data:", error);
      setResponseMessage({
        type: "error",
        text:
          error.message || "Failed to reset account data. Please try again.",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setResponseMessage(null);
  };

  const handleDeleteAccount = () => {
    setNextPageAfterReauth("/dashboard/delete-account-confirm");
    navigate("/dashboard/reauthenticate");
  };

  // Premium + billing portals
  const handlePremiumSubscription = async (planId) => {
    try {
      setIsLoadingPlan(planId);
      await redirectToCheckout(planId, 'month');
    } catch (error) {
      console.error("Premium subscription error:", error);
      setModalTitle("Premium Subscription Error");
      setModalContent("Subscription failed: " + error.message);
      setShowModal(true);
    } finally {
      setIsLoadingPlan(null);
    }
  };

  const handleManageSubscription = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('No active session');
  
      const { data, error } = await supabase.functions.invoke(
        'create-billingportal-session',
        {
          // no body required
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        }
      );
  
      if (error) throw error;
      if (!data?.url) throw new Error('No billing portal URL returned');
      window.location.href = data.url;
    } catch (error) {
      console.error('Error managing subscription:', error);
      setModalTitle('Subscription Management Error');
      setModalContent('Failed to access billing portal: ' + (error.message || 'Unknown error'));
      setShowModal(true);
    }
  };

  const aiLimit = quota?.limit;
  const aiUsed = quota?.used ?? 0;
  const aiProgressPercent = aiLimit ? Math.min(100, ((aiUsed || 0) / (aiLimit || 1)) * 100) : 0;
  const aiResets = quota?.resets_at;
  const shareTypeLabels = {
    screen_result: "Screen Result",
    backtest: "Backtest",
    seasonality_result: "Seasonality Result",
    chart_result: "Chart Result",
  };
  const profileSections = [
    { id: "backtest-configs", label: "Backtest Configurations", icon: FiSliders },
    { id: "watchlists", label: "Watchlists", icon: FiList },
    { id: "shared-links", label: "Shared Links", icon: FiLink },
    { id: "preferences", label: "Preferences", icon: FiSettings },
    { id: "account", label: "User Information & Account Data", icon: FiUser },
  ];

  return (
    <div className="profile-container">
      <div className="profile-header">
        <h1>User Profile</h1>
        <p>Manage your account settings and subscription</p>
      </div>

      {currentUser ? (
        <div className="profile-layout">
          <aside className="profile-sidebar">
            <div className="profile-sidebar-title">Profile Sections</div>
            <div className="profile-sidebar-nav">
              {profileSections.map((section) => {
                const Icon = section.icon;
                const isActive = activeSection === section.id;
                return (
                  <button
                    key={section.id}
                    type="button"
                    className={`profile-sidebar-item ${isActive ? "active" : ""}`}
                    onClick={() => setActiveSection(section.id)}
                    aria-pressed={isActive}
                  >
                    <Icon size={18} />
                    <span>{section.label}</span>
                  </button>
                );
              })}
            </div>
          </aside>

          <div className="profile-main">
            {activeSection === "backtest-configs" && <BacktestConfigsPage embedded />}

            {activeSection === "watchlists" && <WatchlistEditor embedded />}

            {activeSection === "shared-links" && (
              <div className="profile-card">
                <div className="section-title">Shared Links</div>

                <div className="info-text">
                  <FiInfo />
                  <span>
                    These links let anyone view shared results. You can revoke access by deleting a link.
                  </span>
                </div>

                {shareLinksLoading ? (
                  <div style={{ marginTop: 12 }}>Loading shared links...</div>
                ) : shareLinksError ? (
                  <div className="warning-text" style={{ marginTop: 12 }}>
                    <FiAlertCircle />
                    <span>{shareLinksError}</span>
                  </div>
                ) : shareLinks.length === 0 ? (
                  <div style={{ opacity: 0.8, marginTop: 8 }}>
                    You have not shared any results yet.
                  </div>
                ) : (
                  <div className="shared-links-list">
                    {shareLinks.map((link) => {
                      const shareUrl = `${window.location.origin}/share/${link.id}`;
                      const typeLabel = shareTypeLabels[link.resource_type] || link.resource_type;
                      const createdDate = link.created_at ? new Date(link.created_at) : null;
                      const createdAt =
                        createdDate && !Number.isNaN(createdDate.getTime())
                          ? createdDate.toLocaleString()
                          : "Unknown date";
                      return (
                        <div className="shared-link-item" key={link.id}>
                          <div className="shared-link-details">
                            <div className="shared-link-heading">
                              <span className="shared-link-title">{typeLabel}</span>
                              <span className="shared-link-meta">Shared {createdAt}</span>
                            </div>
                            <a
                              className="shared-link-url"
                              href={shareUrl}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {shareUrl}
                            </a>
                            <div className="shared-link-meta">
                              Resource ID{" "}
                              <span className="shared-link-id">{link.resource_id}</span>
                            </div>
                          </div>
                          <div className="shared-link-actions">
                            <button
                              type="button"
                              className="btn danger-btn shared-link-action"
                              onClick={() => handleDeleteShareLink(link.id)}
                              disabled={shareLinksDeleting[link.id]}
                            >
                              <FiTrash2 /> {shareLinksDeleting[link.id] ? "Deleting..." : "Delete"}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {activeSection === "preferences" && (
              <div className="profile-card">
                <div className="section-title">Preferences</div>

                <div className="info-text">
                  <FiInfo />
                  <span>
                    Customize your experience with hints and tutorials to help you get started.
                  </span>
                </div>

                {settingsLoading ? (
                  <div style={{ marginTop: 12 }}>Loading preferences…</div>
                ) : userSettings ? (
                  <>
                    <div className="form-group">
                      <label className="toggle-container">
                        <input
                          type="checkbox"
                          checked={userSettings.onboarding_enabled}
                          onChange={handleToggleOnboarding}
                        />
                        <span className="toggle-label">
                          Show onboarding hints and blinking guides
                          {userSettings.action_count > 0 && (
                            <span style={{ opacity: 0.7, fontSize: '0.9em', marginLeft: 8 }}>
                              ({userSettings.action_count} strategies/backtests run)
                            </span>
                          )}
                        </span>
                      </label>
                      <label className="toggle-container" style={{ marginTop: 12 }}>
                        <input
                          type="checkbox"
                          checked={devModeEnabled}
                          onChange={handleToggleDevMode}
                        />
                        <span className="toggle-label">
                          Enable developer mode (local)
                          <span style={{ display: 'block', opacity: 0.7, fontSize: '0.9em' }}>
                            Shows JSON runner panel on dashboard; stored only in this browser.
                          </span>
                        </span>
                      </label>
                      <label className="toggle-container" style={{ marginTop: 12 }}>
                        <input
                          type="checkbox"
                          checked={expertModeEnabled}
                          onChange={handleToggleExpertMode}
                        />
                        <span className="toggle-label">
                          Enable expert mode (local)
                          <span style={{ display: 'block', opacity: 0.7, fontSize: '0.9em' }}>
                            Adds a download button for rebalancing return series; stored only in this browser.
                          </span>
                        </span>
                      </label>
                    </div>

                    <div className="form-group">
                      <div className="debug-ticker-title">Default formula debug ticker</div>
                      <div className="debug-ticker-row">
                        <div className="debug-ticker-value">
                          {userSettings.debug_ticker || "AAPL"}
                        </div>
                        <button
                          type="button"
                          className="btn secondary-btn debug-ticker-button"
                          onClick={handleStartDebugTickerEdit}
                        >
                          Change
                        </button>
                      </div>
                      <div className="debug-ticker-note">
                        Used to preview live formula values in the Strategy Builder.
                      </div>

                      {debugTickerEditing && (
                        <div className="debug-ticker-editor">
                          <div className="form-inline">
                            <input
                              className="input"
                              value={debugTickerDraft}
                              onChange={(e) => setDebugTickerDraft(e.target.value)}
                              placeholder="Search tickers (e.g., AAPL)"
                            />
                            <button
                              type="button"
                              className="btn primary-btn debug-ticker-button"
                              onClick={handleSaveDebugTicker}
                            >
                              Save
                            </button>
                          </div>
                          <button
                            type="button"
                            className="btn secondary-btn debug-ticker-button"
                            onClick={handleCancelDebugTickerEdit}
                          >
                            Cancel
                          </button>
                          {symbolsLoading ? (
                            <div className="debug-ticker-status">Loading symbols…</div>
                          ) : symbolsError ? (
                            <div className="debug-ticker-status error">{symbolsError}</div>
                          ) : filteredSymbols.length === 0 ? (
                            <div className="debug-ticker-status">No matches found.</div>
                          ) : (
                            <div className="debug-ticker-results">
                              {filteredSymbols.map((symbol) => (
                                <button
                                  key={symbol.ticker}
                                  type="button"
                                  className="debug-ticker-option"
                                  onClick={() => setDebugTickerDraft(symbol.ticker)}
                                >
                                  <div className="debug-ticker-option-title">{symbol.ticker}</div>
                                  <div className="debug-ticker-option-sub">
                                    {symbol.company_name}
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div style={{ opacity: 0.8, marginTop: 8 }}>
                    Unable to load preferences.
                  </div>
                )}
              </div>
            )}

            {activeSection === "account" && (
              <div className="profile-cards-wrapper">
                <div className="profile-card">
                  <div className="section-title">User Information</div>

                  <div className="user-info-item">
                    <FiUser />
                    <strong>Email:</strong> {currentUser.email}
                  </div>

                  {aiLimit && (
                    <div className="quota-section">
                      <div className="quota-bar-container">
                        <div
                          className={`quota-bar-fill ${
                            aiProgressPercent > 80
                              ? "quota-high"
                              : aiProgressPercent > 50
                              ? "quota-medium"
                              : "quota-low"
                          }`}
                          style={{ width: `${aiProgressPercent}%` }}
                        />
                      </div>
                      <div className="info-text">
                        <FiInfo />
                        <span>
                          AI credits this month: {aiUsed} / {aiLimit}
                          {aiResets ? ` · resets ${aiResets}` : ""}
                        </span>
                      </div>
                    </div>
                  )}

                  {isPremium ? (
                    <>
                      <div className="premium-text">
                        <FiCheckCircle />
                        <span>Premium Subscription (Extended Backtest Duration)</span>
                      </div>

                      <div className="info-text">
                        <FiInfo />
                        <span>
                          You can backtest across many years of historical data and enjoy priority support.
                        </span>
                      </div>

                      <div className="form-group">
                        <button
                          className="btn primary-btn"
                          onClick={handleManageSubscription}
                        >
                          <FiCreditCard /> Manage Subscription
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="freeplan-text">
                        <strong>Free Plan</strong>
                        <p>Backtest the last 3 years of historical data</p>
                      </div>

                      <div className="premium-plans-section">
                        <div className="premium-plans-header">
                          <h3 className="premium-plans-title">Unlock Premium Features</h3>
                          <p className="premium-plans-subtitle">
                            {hasUsedTrial ? (
                              <>All paid plans include a <strong>30-day money-back guarantee</strong></>
                            ) : (
                              <>All paid plans include a <strong>7-day free trial</strong> and a <strong>30-day money-back guarantee</strong></>
                            )}
                          </p>
                        </div>

                        <div className="premium-plans-grid">
                          {[
                            {
                              id: 'starter',
                              name: 'Starter',
                              price: 29,
                              badge: '7 Years',
                              features: [
                                'Backtest up to 7 years',
                                '25 Strategies',
                                '25 Watchlists',
                                '100 Saved Backtests',
                                '90-day backtest retention',
                                '100 AI credits / month (1 credit = 1 chat request)'
                              ],
                              highlighted: false,
                            },
                            {
                              id: 'pro',
                              name: 'Pro',
                              price: 49,
                              badge: 'Most Popular',
                              features: [
                                'Backtest up to 15 years',
                                '100 Strategies',
                                '100 Watchlists',
                                '500 Saved Backtests',
                                '1-year backtest retention',
                                '200 AI credits / month (1 credit = 1 chat request)',
                                'Priority support'
                              ],
                              highlighted: true,
                            },
                            {
                              id: 'ultimate',
                              name: 'Ultimate',
                              price: 99,
                              badge: 'Maximum History',
                              features: [
                                'Backtest up to 25 years',
                                '1,000 Strategies',
                                '500 Watchlists',
                                '2,000 Saved Backtests',
                                'Unlimited backtest retention',
                                '1,000 AI credits / month (1 credit = 1 chat request)',
                                'Premium support'
                              ],
                              highlighted: false,
                            }
                          ].map((plan) => (
                            <div
                              key={plan.id}
                              className={`premium-plan-card ${plan.highlighted ? 'highlighted' : ''}`}
                            >
                              <div className="plan-badge">{plan.badge}</div>

                              <h4 className="plan-name">{plan.name}</h4>

                              <div className="plan-price">
                                <span className="price-amount">${plan.price}</span>
                                <span className="price-period">/month</span>
                              </div>

                              <ul className="plan-features">
                                {plan.features.map((feature, idx) => (
                                  <li key={idx} className="feature-item">
                                    <FiCheckCircle className="feature-icon" />
                                    <span>{feature}</span>
                                  </li>
                                ))}
                              </ul>

                              <button
                                onClick={() => handlePremiumSubscription(plan.id)}
                                disabled={isLoadingPlan !== null}
                                className={`plan-cta-button ${plan.highlighted ? 'highlighted-button' : 'default-button'}`}
                                style={{ cursor: isLoadingPlan === plan.id ? 'not-allowed' : 'pointer', opacity: isLoadingPlan === plan.id ? 0.6 : 1 }}
                              >
                                {isLoadingPlan === plan.id ? 'Loading...' : (hasUsedTrial ? 'Subscribe Now' : 'Start Free Trial')}
                              </button>

                              <p className="plan-note">{hasUsedTrial ? 'Cancel anytime' : '7-day free trial · Cancel anytime'}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}

                  <div className="form-group">
                    <button className="btn secondary-btn" onClick={handleSignOut}>
                      <FiLogOut /> Sign Out
                    </button>
                  </div>
                </div>

                {currentUser?.isEmailSignIn && (
                  <div className="profile-card">
                    <div className="section-title">Security & Email</div>

                    {!currentUser.emailVerified && (
                      <>
                        <div className="warning-text">
                          <FiAlertCircle />
                          <span>
                            Your email is not verified. Please verify your email for
                            account security.
                          </span>
                        </div>
                        <div className="form-group">
                          <button
                            className="btn secondary-btn"
                            onClick={requestEmailVerification}
                          >
                            <FiMail /> Resend Verification Email
                          </button>
                        </div>
                      </>
                    )}

                    <div className="form-group">
                      <button
                        className="btn secondary-btn"
                        onClick={handleChangeEmailClick}
                      >
                        <FiMail /> Change Email
                      </button>
                    </div>

                    <div className="form-group">
                      <button
                        className="btn secondary-btn"
                        onClick={handleChangePasswordClick}
                      >
                        <FiLock /> Change Password
                      </button>
                    </div>
                  </div>
                )}

                <div className="profile-card">
                  <div className="section-title">Account Data</div>

                  <div className="warning-text">
                    <FiAlertCircle />
                    <span>
                      The following actions will affect your stored data and cannot be
                      undone.
                    </span>
                  </div>

                  <div className="form-group">
                    <button className="btn danger-btn" onClick={handleResetAccountData}>
                      <FiRefreshCw /> Reset Account Data
                    </button>
                  </div>
                  <div className="form-group">
                    <button className="btn danger-btn" onClick={handleDeleteAccount}>
                      <FiTrash2 /> Delete Account
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="profile-card">
          <div className="warning-text">
            <FiAlertCircle />
            <span>No user is logged in. Please log in to view your profile.</span>
          </div>
        </div>
      )}

      {/* Reset Account Confirmation Modal */}
      <Modal
        isOpen={isModalOpen}
        title="Reset Account Data"
        message="Are you sure you want to reset your account data? All your saved strategies, screen results, and backtest results will be permanently deleted. This action cannot be undone."
        onClose={handleCloseModal}
        onConfirm={handleResetConfirm}
        isProcessing={isProcessing}
        responseMessage={responseMessage}
        variant="confirm"
        confirmLabel="Yes, Reset Data"
        cancelLabel="Cancel"
      />

      {/* Generic info/error modal (copy success, API errors, etc.) */}
      {showModal && (
        <Modal
          isOpen={showModal}
          title={modalTitle}
          message={modalContent}
          onClose={() => setShowModal(false)}
          variant="info"
        />
      )}
    </div>
  );
}

export default Profile;
